import { Rect } from 'fabric';
import { applyHierarchicalSnap, ENDPOINT_SNAP_DISTANCE } from './snapUtils';
import { GARDEN_OBJECT_TYPES, createGardenObject } from '../../../constants/gardenObjectTypes';
import { getObjectTypeById } from '../../../config';

/**
 * RectangleTool - Handles rectangle drawing logic
 *
 * Features:
 * - Draw rectangles by dragging
 * - Snap-to-grid support (via snapUtils)
 * - Snap-to-endpoint support for corners (via snapUtils)
 * - Shift: Constrain to square (1:1 ratio)
 * - Alt: Draw from center
 * - Ctrl: Temporarily disable grid snap (endpoint snap still works)
 * - Auto-switch to select mode after drawing
 */

/**
 * Calculate rectangle bounds with modifiers
 *
 * @param {object} start - Start point {x, y}
 * @param {object} current - Current mouse point {x, y}
 * @param {boolean} shiftPressed - Constrain to square
 * @param {boolean} altPressed - Draw from center
 * @param {boolean} ctrlPressed - Disable grid snap (endpoint snap still active)
 * @param {number} gridSize - Grid size in pixels
 * @param {boolean} snapEnabled - Whether snap is enabled
 * @param {fabric.Canvas} canvas - Canvas instance for endpoint snapping (optional)
 * @returns {object} Rectangle bounds {left, top, width, height, snappedTo}
 */
export const calculateRectangleBounds = (
  start,
  current,
  shiftPressed,
  altPressed,
  ctrlPressed,
  gridSize,
  snapEnabled,
  canvas = null
) => {
  // Apply hierarchical snap to start point (endpoint → grid → raw)
  const startSnap = applyHierarchicalSnap(
    start,
    canvas,
    gridSize,
    snapEnabled,
    ctrlPressed,
    ENDPOINT_SNAP_DISTANCE
  );

  // Apply hierarchical snap to current point
  const currentSnap = applyHierarchicalSnap(
    current,
    canvas,
    gridSize,
    snapEnabled,
    ctrlPressed,
    ENDPOINT_SNAP_DISTANCE
  );

  let { x: startX, y: startY } = startSnap.point;
  let { x: currentX, y: currentY } = currentSnap.point;

  // Calculate initial width and height
  let width = currentX - startX;
  let height = currentY - startY;

  // Shift: Constrain to square
  if (shiftPressed) {
    const size = Math.max(Math.abs(width), Math.abs(height));
    width = width < 0 ? -size : size;
    height = height < 0 ? -size : size;
  }

  // Calculate position (top-left corner)
  let left = startX;
  let top = startY;

  // Alt: Draw from center
  if (altPressed) {
    left = startX - width / 2;
    top = startY - height / 2;
  }

  // Adjust for negative width/height (dragging up/left)
  if (width < 0) {
    left = left + width;
    width = Math.abs(width);
  }
  if (height < 0) {
    top = top + height;
    height = Math.abs(height);
  }

  // Return bounds with snap information
  return {
    left,
    top,
    width,
    height,
    snappedTo: currentSnap.snappedTo, // 'endpoint', 'grid', or 'none'
    snapTarget: currentSnap.target, // Fabric.js object if endpoint snap
  };
};

/**
 * Create a new rectangle with default styling
 *
 * @param {object} bounds - Rectangle bounds {left, top, width, height}
 * @param {boolean} isActive - Whether rectangle is selected/active
 * @param {string|null} objectTypeId - Garden object type ID (e.g., 'mauer', 'tor', 'beet')
 * @returns {Rect} Fabric.js Rect object with garden object metadata
 */
export const createRectangle = (bounds, isActive = true, objectTypeId = null) => {
  const { left, top, width, height } = bounds;

  // Get default style from config if objectTypeId provided
  let fillColor, strokeColor, strokeWidth = 2, opacity = 1;
  let objectConfig = null;

  if (objectTypeId) {
    objectConfig = getObjectTypeById(objectTypeId);
    if (objectConfig && objectConfig.defaultStyle) {
      const style = objectConfig.defaultStyle;
      fillColor = style.fill;
      strokeColor = style.stroke;
      strokeWidth = style.strokeWidth || 2;
      opacity = style.opacity !== undefined ? style.opacity : 1;
    }
  }

  // Fallback to neutral colors if no config found (for Zeichnen tab usage)
  if (!fillColor) {
    fillColor = 'rgba(107, 114, 128, 0.2)'; // Gray-500 with 20% opacity (neutral)
  }

  if (!strokeColor) {
    strokeColor = '#6B7280'; // Gray-500 (neutral)
  }

  const rect = new Rect({
    left,
    top,
    originX: 'left',
    originY: 'top',
    width,
    height,
    fill: fillColor,
    stroke: strokeColor,
    strokeWidth: strokeWidth,
    opacity: opacity,
    strokeUniform: true, // Maintain stroke width during scaling
    lockUniScaling: false, // Allow non-uniform scaling (corner handles resize freely)
    uniformScaling: false, // Disable uniform scaling constraint
    noScaleCache: true,   // Skip cache regeneration on viewport zoom (acceptable tradeoff at 1x-3x)
  });

  // Apply garden object type system
  createGardenObject(GARDEN_OBJECT_TYPES.RECTANGLE, rect, {
    // Keep legacy data for backward compatibility
    data: {
      shapeType: 'rectangle', // Renamed from 'type' to avoid conflict with garden object properties
      isActive,
      objectTypeId: objectTypeId, // Store the garden object type ID
      gardenObjectType: objectTypeId, // Add both for compatibility
      createdAt: new Date().toISOString(),
      // Store original colors for selection/deselection
      originalFill: fillColor,
      originalStroke: strokeColor,
      originalStrokeWidth: strokeWidth,
      originalOpacity: opacity,
      // Add config metadata if available
      ...(objectConfig && {
        isPlantable: objectConfig.isPlantable,
        castsShadow: objectConfig.castsShadow,
      }),
    },
  });

  // Set rotation cursor on the mtr (middle-top-rotation) control
  // Use 'grab' cursor to indicate draggable rotation
  if (rect.controls && rect.controls.mtr) {
    rect.controls.mtr.cursorStyleHandler = () => 'grab';
  }

  return rect;
};

/**
 * Update rectangle appearance based on selection state
 *
 * @param {Rect} rect - Fabric.js Rect object
 * @param {boolean} isActive - Whether rectangle is selected/active
 */
export const updateRectangleAppearance = (rect, isActive) => {
  if (!rect || !rect.data) return;

  if (isActive) {
    // Selected: Use emerald-500 green
    rect.set({
      fill: 'rgba(16, 185, 129, 0.2)',
      stroke: '#10B981',
    });
  } else {
    // Deselected: Restore original config colors from object type definition
    const objectTypeId = rect.data.objectTypeId || rect.data.gardenObjectType;

    if (objectTypeId) {
      // Get config for this object type
      const objectConfig = getObjectTypeById(objectTypeId);

      if (objectConfig && objectConfig.defaultStyle) {
        // Restore colors from config
        rect.set({
          fill: objectConfig.defaultStyle.fill || 'rgba(107, 114, 128, 0.2)',
          stroke: objectConfig.defaultStyle.stroke || '#6B7280',
          strokeWidth: objectConfig.defaultStyle.strokeWidth || 2,
          opacity: objectConfig.defaultStyle.opacity !== undefined ? objectConfig.defaultStyle.opacity : 1,
        });
      } else {
        // Fallback to neutral colors if no config found
        rect.set({
          fill: 'rgba(107, 114, 128, 0.2)',
          stroke: '#6B7280',
          strokeWidth: 2,
          opacity: 1,
        });
      }
    } else {
      // No object type (legacy or Zeichnen tab usage) - use neutral colors
      rect.set({
        fill: 'rgba(107, 114, 128, 0.2)',
        stroke: '#6B7280',
        strokeWidth: 2,
        opacity: 1,
      });
    }
  }

  // Update data
  if (rect.data) {
    rect.data.isActive = isActive;
  }
};

/**
 * Get rectangle dimensions in cm
 *
 * @param {Rect} rect - Fabric.js Rect object
 * @param {number} pixelsPerCm - Conversion factor (default: 1 pixel = 1 cm)
 * @returns {object} Dimensions {widthCm, heightCm, areaSqm}
 */
export const getRectangleDimensions = (rect, pixelsPerCm = 1) => {
  const widthCm = (rect.width * rect.scaleX) / pixelsPerCm;
  const heightCm = (rect.height * rect.scaleY) / pixelsPerCm;
  const areaSqm = (widthCm * heightCm) / 10000; // Convert cm² to m²

  return {
    widthCm: Math.round(widthCm * 100) / 100, // Round to 2 decimals
    heightCm: Math.round(heightCm * 100) / 100,
    areaSqm: Math.round(areaSqm * 100) / 100,
  };
};

/**
 * Update rectangle dimensions (for properties panel editing)
 *
 * @param {Rect} rect - Fabric.js Rect object
 * @param {object} dimensions - New dimensions {widthCm, heightCm}
 * @param {number} pixelsPerCm - Conversion factor
 */
export const updateRectangleDimensions = (rect, dimensions, pixelsPerCm = 1) => {
  const { widthCm, heightCm } = dimensions;

  // Calculate new scale factors
  const newWidth = widthCm * pixelsPerCm;
  const newHeight = heightCm * pixelsPerCm;

  rect.set({
    width: newWidth,
    height: newHeight,
    scaleX: 1,
    scaleY: 1,
  });
};

/**
 * Update rectangle position (for properties panel editing)
 *
 * @param {Rect} rect - Fabric.js Rect object
 * @param {object} position - New position {x, y} in cm
 * @param {number} pixelsPerCm - Conversion factor
 */
export const updateRectanglePosition = (rect, position, pixelsPerCm = 1) => {
  const { x, y } = position;

  rect.set({
    left: x * pixelsPerCm,
    top: y * pixelsPerCm,
  });
};
